import { Component, OnInit, OnDestroy, HostListener } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { ToastController } from "@ionic/angular";
import { UtilService } from "../../shared/services/util.service";
import { MidYearEvaluation, FreeEvaluationStatus } from "../_interfaces/mid-year-evaluation.interface";
import { FreeEvaluationService } from "../free-evaluation-mid-year.service";
import { TranslationService } from "src/app/shared/services/translation.service";
import { QuestionConfig, ProcessConfig, QuestionGroup, QuestionTypeEnum } from "src/app/shared/interfaces/process-config.interface";
import { BaseClass } from "src/app/shared/classes/base";
import { UntypedFormGroup, UntypedFormControl, Validators } from "@angular/forms";
import { EvaluatedUser } from "../_interfaces/evaluated-user.interface";
import { distinctUntilChanged, pairwise, startWith } from "rxjs/operators";
import { QuestionAttributePipe } from "src/app/shared/pipes/question-attribute.pipe";
import { EndYearEvaluation } from "src/app/free-evaluation-end-year/_interfaces/end-year-evaluation.interface";
import { QuestionGroupAttributesPipe } from "src/app/shared/pipes/question-group-attributes.pipe";
import { ConfigService } from '../../shared/services/multi-clients/config.service';

@Component({
    selector: "app-manage-evaluation-mid-year",
    templateUrl: "./manage-evaluation-mid-year.page.html",
    styleUrls: ["./manage-evaluation-mid-year.page.scss"],
})
export class ManageEvaluationMidYearPage extends BaseClass implements OnDestroy, OnInit {
    public FreeEvaluationStatus = FreeEvaluationStatus;
    public processConfig: ProcessConfig;
    public allQuestions: Array<QuestionConfig> = [];
    public editingEvaluation: MidYearEvaluation;
    public formGroup: UntypedFormGroup;
    public user: EvaluatedUser;
    public questionsType: any;
    public highlightSelfOrManagerEvalAnswer: any;

    private _cycleId: number;
    private _cycleParticipantId: number;
    private _subjectUserId: string;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _configService: ConfigService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _evaluationService: FreeEvaluationService,
        private _questionGroupsAttributePipe: QuestionGroupAttributesPipe,
        private _questionAttributePipe: QuestionAttributePipe,
        private _utilsService: UtilService,
    ) {
        super(_toastController, _translationService);
    }

    ngOnInit() {
        this.questionsType = this._configService.settings.features.questionTypeAvailability.question;
        this.highlightSelfOrManagerEvalAnswer = this._configService.settings.features.highlightSelfOrManagerEvalAnswer;
    }

    ionViewWillEnter() {
        this.processConfig = this.readFromStorage("FreeEvaluationMidYear_ProcessConfig");
        if (!this.processConfig) {
            this.goBack();
            return;
        }
        this.user = this.readFromStorage("FreeEvaluationMidYear_EvaluatedUser");

        this.editingEvaluation = this.readFromStorage("FreeEvaluationMidYear_Evaluation");
        if (!this.user) {
            this.user = this.editingEvaluation.subjectUser;
        }

        if (this.processConfig.questions) {
            this.allQuestions = [...this.allQuestions, ...this.processConfig.questions];
            let groupQuestions = this._questionAttributePipe.transform(this.processConfig.questions, this.user.attributes);
            this.processConfig.questions = groupQuestions.filter((q) => !q.hideQuestion);
        } else {
            let questions: QuestionConfig[] = [];
            this.processConfig.questionsGroups.forEach((group) => {
                questions = [...questions, ...group.questions];
                group.hasCalculateQuestions = this.hasCalculateType(group);
                questions = this._questionAttributePipe.transform(questions, this.user.attributes);
                let groupQuestions = this._questionAttributePipe.transform(group.questions, this.user.attributes);
                group.questions = groupQuestions.filter((q) => !q.hideQuestion);
            });
            this.allQuestions = [...this.allQuestions, ...questions];
        }

        this.processConfig.questionsGroups = this.processConfig.questionsGroups.sort((a, b) => a.order - b.order);

        this.formGroup = this._createFormGroup(this.processConfig.questionsGroups, this.editingEvaluation);

        if (this.processConfig.questionsGroups) {
            this.processConfig.questionsGroups.forEach((group) => {
                group.questions = this._utilsService.groupQuestions(group.questions);
            });
        }

        this.formGroup.valueChanges.pipe(startWith(this.formGroup.value), distinctUntilChanged(), pairwise()).subscribe(async ([previous, current]) => {
            if (JSON.stringify(previous) === JSON.stringify(current)) return;
            const valuesToSet = await this._utilsService.calculateRelatedQuestions(current, this.allQuestions, previous);
            valuesToSet.forEach((result) => {
                this.formGroup.get(result.field).setValue(result.value, { emitEvent: true, onlySelf: true });
            });
        });
    }

    public goBack(): void {
        this._router.navigate(["free-evaluation-mid-year/" + this._cycleId], { state: { firstView: false } });
    }

    public saveEvaluation(formGroup: UntypedFormGroup): void {
        const evaluation: MidYearEvaluation = formGroup.getRawValue();

        if (this._validateFormGroup(evaluation)) {
            evaluation.id ? this._updateEvaluation(evaluation) : this._createEvaluation(evaluation);
        } else {
            formGroup["missingFields"] = true;
            this.notify(this._translationService.getTranslation("generic.missingFields"), "danger");
            return;
        }
    }

    private _validateFormGroup(evaluation: MidYearEvaluation): boolean {
        if (this.processConfig.questionsGroups && this.processConfig.questionsGroups.length > 0) {
            return this.processConfig.questionsGroups.some((group) => {
                return group.questions.some((question) => {
                    const controlName = "field" + question.currentIndex;
                    return evaluation[controlName] && evaluation[controlName].trim() !== "";
                });
            });
        } else {
            return this.processConfig.questions.some((question) => {
                const controlName = "field" + question.currentIndex;
                return evaluation[controlName] && evaluation[controlName].trim() !== "";
            });
        }
    }

    private _createEvaluation(evaluation: MidYearEvaluation): void {
        this._evaluationService.saveMidYearEvaluation(evaluation).subscribe(
            () => {
                this.notify(this._translationService.getTranslation("generic.saveSuccess"), "success");
                this.goBack();
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _updateEvaluation(evaluation: MidYearEvaluation): void {
        this._evaluationService.updateMidYearEvaluation(evaluation).subscribe(
            () => {
                this.notify(this._translationService.getTranslation("generic.saveSuccess"), "success");
                this.goBack();
            },
            (error) => this.notify(this.getErrorNotification(error), "danger"),
        );
    }

    private _createFormGroup(groups: Array<QuestionGroup>, editingEvaluation: EndYearEvaluation = null): UntypedFormGroup {
        const formGroup = new UntypedFormGroup({
            cycleParticipantId: new UntypedFormControl(this._cycleParticipantId),
            subjectUserId: new UntypedFormControl(this._subjectUserId),
        });

        if (editingEvaluation) {
            formGroup.addControl("id", new UntypedFormControl(editingEvaluation.id));
        }
        const displayedGroups = this._questionGroupsAttributePipe.transform(groups, this.user.attributes);
        groups.forEach((group) => {
            group.questions.forEach((question) => {
                const controlName = "field" + question.order;
                const validators = displayedGroups.some((g) => g.id === group.id) && (question.required || question.goal) ? [Validators.required] : [];
                formGroup.addControl(
                    controlName,
                    new UntypedFormControl(
                        {
                            value: editingEvaluation ? editingEvaluation[controlName] : "",
                            disabled: editingEvaluation && editingEvaluation.status === FreeEvaluationStatus.Sent,
                        },
                        validators,
                    ),
                );

                question.currentIndex = question.order;
            });
        });
        if (this.processConfig.questions) {
            this.processConfig.questions.forEach((question) => {
                const controlName = "field" + question.order;
                const validators = question.required ? [Validators.required] : [];
                formGroup.addControl(
                    controlName,
                    new UntypedFormControl(
                        {
                            value: editingEvaluation ? editingEvaluation[controlName] : "",
                            disabled: editingEvaluation && editingEvaluation.status === FreeEvaluationStatus.Sent,
                        },
                        validators,
                    ),
                );
                question.currentIndex = question.order;
            });
        }

        return formGroup;
    }

    public showRequiredBadge(formGroup: UntypedFormGroup, index: number): boolean {
        return formGroup["missingFields"] && !formGroup.get("field" + index).valid;
    }

    public setFormControlValue(formControl: string, value: any): void {
        if (!this.formGroup.get(formControl).disabled) this.formGroup.get(formControl).setValue(value);
    }

    private hasCalculateType(group: QuestionGroup) {
        return group.questions.some((q) => q.typeConfig.type === QuestionTypeEnum.Calculate);
    }

    @HostListener("window:beforeunload")
    ngOnDestroy() {
        localStorage.removeItem("FreeEvaluationMidYear_ProcessConfig");
        localStorage.removeItem("FreeEvaluationMidYear_Evaluation");
        localStorage.removeItem("FreeEvaluationMidYear_EvaluatedUser");
    }
}
